import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  CheckCircle,
  CircleNotch,
  CurrencyDollar,
  File,
  FileText,
  FileXls,
  HardDrive,
  Image,
  Info,
  ListBullets,
  Presentation,
  Question,
  Table,
  Trash,
  UploadSimple,
  WarningCircle,
  X
} from '@phosphor-icons/react';
// CSV Type Configuration
const CSV_TYPES = {
  pricing_table: {
    label: 'Pricing Table',
    description: 'Product prices, service tiers, fees',
    icon: CurrencyDollar,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    required_columns: ['item_name', 'price'],
    example: 'item_name, price, category, notes'
  },
  faq_dataset: {
    label: 'FAQ Dataset',
    description: 'Structured Q&A pairs',
    icon: Question,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    required_columns: ['question', 'answer'],
    example: 'question, answer, category'
  },
  lookup_table: {
    label: 'Lookup / Reference Table',
    description: 'Mappings, codes, rules, SKUs',
    icon: ListBullets,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    required_columns: [],
    example: 'Any columns (flexible structure)'
  }
};

const KnowledgeBaseUploader = ({ agentId, onUpdate, knowledgeExpectations }) => {
  const { activeOrganization } = useOrganization();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [totalSize, setTotalSize] = useState(0);
  const [maxSizeLimit, setMaxSizeLimit] = useState(100 * 1024 * 1024);
  const [dragActive, setDragActive] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null);
  const [descriptionInput, setDescriptionInput] = useState('');
  
  // CSV UploadSimple State
  const [pendingCsvFile, setPendingCsvFile] = useState(null);
  const [showCsvTypeModal, setShowCsvTypeModal] = useState(false);
  const [selectedCsvType, setSelectedCsvType] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);

  const FILE_TYPE_ICONS = {
    'pdf': { icon: FileText, color: 'text-red-400' },
    'docx': { icon: FileText, color: 'text-blue-400' },
    'xlsx': { icon: FileXls, color: 'text-green-400' },
    'pptx': { icon: Presentation, color: 'text-orange-400' },
    'csv': { icon: Table, color: 'text-emerald-400' },
    'image': { icon: Image, color: 'text-purple-400' },
    'file': { icon: File, color: 'text-gray-400' },
  };

  // Fetch knowledge base files
  const fetchKnowledgeBase = useCallback(async () => {
    if (!agentId || !activeOrganization?.organization_id) return;
    
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/knowledge`,
        { method: 'GET' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setFiles(data.files || []);
        setTotalSize(data.total_size || 0);
        setMaxSizeLimit(data.max_size_limit || 100 * 1024 * 1024);
      }
    } catch (err) {
      console.error('Error fetching knowledge base:', err);
    } finally {
      setLoading(false);
    }
  }, [agentId, activeOrganization?.organization_id]);

  useEffect(() => {
    fetchKnowledgeBase();
  }, [fetchKnowledgeBase]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Parse CSV for preview
  const parseCsvPreview = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0]?.split(',').map(h => h.trim().toLowerCase()) || [];
        const previewRows = lines.slice(1, 6).map(line => {
          const values = line.split(',');
          const row = {};
          headers.forEach((h, i) => row[h] = values[i]?.trim() || '');
          return row;
        });
        
        resolve({
          headers,
          rowCount: lines.length - 1,
          previewRows
        });
      };
      reader.readAsText(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (selectedFiles) => {
    if (!selectedFiles.length || !agentId) return;

    const filesToUpload = [];
    const csvFiles = [];

    for (const file of Array.from(selectedFiles)) {
      const ext = file.name.split('.').pop().toLowerCase();
      
      if (ext === 'csv') {
        csvFiles.push(file);
      } else {
        filesToUpload.push({ file, csvType: null });
      }
    }

    // UploadSimple non-CSV files immediately
    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload);
    }

    // Handle CSV files - show type selector modal
    if (csvFiles.length > 0) {
      const preview = await parseCsvPreview(csvFiles[0]);
      setCsvPreview(preview);
      setPendingCsvFile(csvFiles[0]);
      setShowCsvTypeModal(true);
    }
  };

  // UploadSimple files
  const uploadFiles = async (filesToUpload) => {
    setUploading(true);
    setError(null);
    setSuccess(null);

    const uploadPromises = filesToUpload.map(async ({ file, csvType }) => {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowedExts = ['pdf', 'docx', 'xlsx', 'pptx', 'csv', 'png', 'jpg', 'jpeg'];
      
      if (!allowedExts.includes(ext)) {
        return { error: `${file.name}: Unsupported file type` };
      }

      if (file.size > 50 * 1024 * 1024) {
        return { error: `${file.name}: File too large (max 50MB)` };
      }

      const formData = new FormData();
      formData.append('file', file);
      if (csvType) {
        formData.append('csv_type', csvType);
      }

      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agents/${agentId}/knowledge/upload`,
          {
            method: 'POST',
            body: formData
          },
          activeOrganization.organization_id
        );

        if (response.ok) {
          return { success: true, filename: file.name };
        } else {
          const err = await response.json();
          return { error: `${file.name}: ${err.detail || 'Upload failed'}` };
        }
      } catch (err) {
        return { error: `${file.name}: Upload failed` };
      }
    });

    const results = await Promise.all(uploadPromises);
    
    const errors = results.filter(r => r.error).map(r => r.error);
    const successes = results.filter(r => r.success);

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }
    
    if (successes.length > 0) {
      setSuccess(`${successes.length} file(s) uploaded successfully`);
      fetchKnowledgeBase();
      if (onUpdate) onUpdate();
    }

    setUploading(false);
    
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);
  };

  // Handle CSV type selection confirmation
  const handleCsvTypeConfirm = async () => {
    if (!selectedCsvType || !pendingCsvFile) return;
    
    setShowCsvTypeModal(false);
    await uploadFiles([{ file: pendingCsvFile, csvType: selectedCsvType }]);
    
    setPendingCsvFile(null);
    setSelectedCsvType(null);
    setCsvPreview(null);
  };

  // Handle file delete
  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}" from knowledge base?`)) return;

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/knowledge/${fileId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        setSuccess('File deleted successfully');
        fetchKnowledgeBase();
        if (onUpdate) onUpdate();
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to delete file');
      }
    } catch (err) {
      setError('Failed to delete file');
    }

    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 3000);
  };

  // Handle description update
  const handleUpdateDescription = async (fileId) => {
    try {
      const formData = new FormData();
      formData.append('description', descriptionInput);

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/knowledge/${fileId}`,
        {
          method: 'PATCH',
          body: formData
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        fetchKnowledgeBase();
        setEditingDescription(null);
        setDescriptionInput('');
      }
    } catch (err) {
      console.error('Error updating description:', err);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const getFileIcon = (fileType) => {
    const config = FILE_TYPE_ICONS[fileType] || FILE_TYPE_ICONS['file'];
    const Icon = config.icon;
    return <Icon size={20} className={config.color} />;
  };

  const getCsvTypeLabel = (csvType) => {
    return CSV_TYPES[csvType]?.label || csvType;
  };

  const usagePercent = maxSizeLimit > 0 ? (totalSize / maxSizeLimit) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotch className="w-6 h-6 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Knowledge Base & Reference Material</h3>
        <p className="text-sm text-gray-400">
          UploadSimple documents, spreadsheets, or catalogues your agent should reference when answering questions or performing tasks.
        </p>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <Info size={12} />
          Files are used as reference knowledge. They do not replace instructions or workflows.
        </p>
      </div>

      {/* Knowledge Expectations Guidance */}
      {knowledgeExpectations?.requires_documents && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <WarningCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium">This agent was designed to reference business documents</p>
              <p className="text-sm text-amber-400/80 mt-1">
                UploadSimple catalogues, pricing, or reference files below for optimal performance.
                {knowledgeExpectations.suggested_sources?.length > 0 && (
                  <span className="block mt-1">
                    Suggested: {knowledgeExpectations.suggested_sources.join(', ')}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Storage Usage */}
      <div className="p-4 bg-[rgba(255,255,255,0.03)] rounded-xl border border-[rgba(255,255,255,0.1)]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <HardDrive size={16} className="text-gray-400" />
            <span className="text-sm text-gray-400">Storage Used</span>
          </div>
          <span className="text-sm text-white font-medium">
            {formatSize(totalSize)} / {formatSize(maxSizeLimit)}
          </span>
        </div>
        <div className="w-full h-2 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-yellow-500' : 'bg-purple-500'
            }`}
            style={{ width: `${Math.min(usagePercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
          <WarningCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400 whitespace-pre-wrap">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
          <CheckCircle size={16} className="text-green-400" />
          <p className="text-sm text-green-400">{success}</p>
        </div>
      )}

      {/* UploadSimple Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
          ${dragActive 
            ? 'border-purple-500 bg-purple-500/10' 
            : 'border-[rgba(255,255,255,0.1)] hover:border-purple-500/50 hover:bg-[rgba(255,255,255,0.02)]'
          }
        `}
      >
        <input
          type="file"
          multiple
          accept=".pdf,.docx,.xlsx,.pptx,.csv,.png,.jpg,.jpeg"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <CircleNotch className="w-10 h-10 text-purple-500 animate-spin" />
            <p className="text-gray-400">Uploading files...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-purple-500/10 flex items-center justify-center">
              <UploadSimple size={24} className="text-purple-400" />
            </div>
            <div>
              <p className="text-white font-medium">Drop files here or click to upload</p>
              <p className="text-gray-500 text-sm mt-1">
                PDF, Word, Excel, PowerPoint, CSV, PNG, JPG (max 50MB each)
              </p>
            </div>
          </div>
        )}
      </div>

      {/* File ListBullets */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-300">
            Uploaded Files ({files.length})
          </h4>
          
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="p-4 bg-[rgba(255,255,255,0.03)] rounded-xl border border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)] transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* File Icon */}
                  <div className="w-10 h-10 rounded-lg bg-[rgba(255,255,255,0.05)] flex items-center justify-center flex-shrink-0">
                    {getFileIcon(file.file_type)}
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium truncate">{file.filename}</p>
                      <span className="text-xs px-2 py-0.5 bg-[rgba(255,255,255,0.1)] rounded text-gray-400 uppercase">
                        {file.file_type}
                      </span>
                      {file.csv_type && (
                        <span className={`text-xs px-2 py-0.5 rounded ${CSV_TYPES[file.csv_type]?.bgColor || 'bg-gray-500/10'} ${CSV_TYPES[file.csv_type]?.color || 'text-gray-400'}`}>
                          {getCsvTypeLabel(file.csv_type)}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{formatSize(file.size)}</span>
                      <span>•</span>
                      <span>{formatDate(file.uploaded_at)}</span>
                      {file.csv_row_count && (
                        <>
                          <span>•</span>
                          <span>{file.csv_row_count} rows</span>
                        </>
                      )}
                    </div>
                    
                    {/* CSV Columns Preview */}
                    {file.csv_columns && file.csv_columns.length > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        <span className="text-gray-500">Columns:</span> {file.csv_columns.slice(0, 6).join(', ')}
                        {file.csv_columns.length > 6 && ` (+${file.csv_columns.length - 6} more)`}
                      </p>
                    )}
                    
                    {/* Content Preview for non-CSV */}
                    {!file.csv_columns && file.content_preview && (
                      <p className="text-xs text-gray-400 mt-2 truncate">
                        {file.content_preview}
                      </p>
                    )}
                    
                    {/* Description */}
                    {editingDescription === file.id ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={descriptionInput}
                          onChange={(e) => setDescriptionInput(e.target.value)}
                          placeholder="Add a description..."
                          className="flex-1 px-3 py-1.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateDescription(file.id)}
                          className="px-3 py-1.5 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingDescription(null);
                            setDescriptionInput('');
                          }}
                          className="p-1.5 text-gray-400 hover:text-white transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : file.description ? (
                      <p 
                        className="text-sm text-gray-400 mt-2 cursor-pointer hover:text-gray-300"
                        onClick={() => {
                          setEditingDescription(file.id);
                          setDescriptionInput(file.description || '');
                        }}
                      >
                        {file.description}
                      </p>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingDescription(file.id);
                          setDescriptionInput('');
                        }}
                        className="text-xs text-purple-400 hover:text-purple-300 mt-2"
                      >
                        + Add description
                      </button>
                    )}
                  </div>
                  
                  {/* Trash Button */}
                  <button
                    onClick={() => handleDelete(file.id, file.filename)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete file"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {files.length === 0 && !loading && (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-[rgba(255,255,255,0.05)] flex items-center justify-center mb-4">
            <FileText size={32} className="text-gray-500" />
          </div>
          <p className="text-gray-400">No files uploaded yet</p>
          <p className="text-sm text-gray-500 mt-1">
            UploadSimple documents your agent can reference
          </p>
        </div>
      )}

      {/* CSV Type Selection Modal */}
      {showCsvTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-[#1A1A1A] rounded-2xl border border-[rgba(255,255,255,0.1)] w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white">Select CSV Type</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Choose how your agent should interpret this CSV file
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCsvTypeModal(false);
                    setPendingCsvFile(null);
                    setSelectedCsvType(null);
                    setCsvPreview(null);
                  }}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* File Info */}
              {pendingCsvFile && (
                <div className="mt-4 p-3 bg-[rgba(255,255,255,0.05)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Table size={16} className="text-emerald-400" />
                    <span className="text-white font-medium">{pendingCsvFile.name}</span>
                    <span className="text-gray-500 text-sm">({formatSize(pendingCsvFile.size)})</span>
                  </div>
                  {csvPreview && (
                    <p className="text-xs text-gray-400 mt-2">
                      {csvPreview.rowCount} rows • Columns: {csvPreview.headers.slice(0, 5).join(', ')}
                      {csvPreview.headers.length > 5 && ` (+${csvPreview.headers.length - 5} more)`}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* CSV Type Options */}
            <div className="p-6 space-y-4">
              {Object.entries(CSV_TYPES).map(([type, config]) => {
                const TypeIcon = config.icon;
                const isSelected = selectedCsvType === type;
                
                // Check if required columns are present
                const missingColumns = config.required_columns.filter(
                  col => !csvPreview?.headers?.includes(col.toLowerCase())
                );
                const hasRequiredColumns = missingColumns.length === 0;
                
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedCsvType(type)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? `${config.borderColor} ${config.bgColor}` 
                        : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                        <TypeIcon size={20} className={config.color} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-semibold">{config.label}</h4>
                          {isSelected && <CheckCircle size={16} className={config.color} />}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{config.description}</p>
                        
                        {config.required_columns.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500">
                              Required columns: <span className="text-gray-400">{config.required_columns.join(', ')}</span>
                            </p>
                            {!hasRequiredColumns && csvPreview && (
                              <p className="text-xs text-amber-400 mt-1">
                                ⚠️ Missing: {missingColumns.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {type === 'lookup_table' && (
                          <p className="text-xs text-gray-500 mt-2">
                            Flexible structure - any columns accepted
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Preview Section */}
            {csvPreview && csvPreview.previewRows.length > 0 && (
              <div className="px-6 pb-4">
                <h4 className="text-sm font-medium text-gray-300 mb-2">Data Preview (first 5 rows)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[rgba(255,255,255,0.1)]">
                        {csvPreview.headers.slice(0, 6).map((header, i) => (
                          <th key={i} className="px-3 py-2 text-left text-gray-400 font-medium">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.previewRows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-b border-[rgba(255,255,255,0.05)]">
                          {csvPreview.headers.slice(0, 6).map((header, j) => (
                            <td key={j} className="px-3 py-2 text-gray-300 truncate max-w-[150px]">
                              {row[header] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-[rgba(255,255,255,0.1)] flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCsvTypeModal(false);
                  setPendingCsvFile(null);
                  setSelectedCsvType(null);
                  setCsvPreview(null);
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCsvTypeConfirm}
                disabled={!selectedCsvType}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                UploadSimple CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseUploader;
